import { useState, useCallback, useEffect } from "react"
import { ActionIcon, Badge, Button, Group, Text, TextInput, Tooltip } from "@mantine/core"
import { useDebouncedValue } from "@mantine/hooks"
import { Trans, useTranslation } from "react-i18next"
import { PageHeader, PageLayout } from "../../../components/crud"
import type { Column } from "../../../components/DataTable"
import { DataTable, useDataTable } from "../../../components/DataTable"
import { useUser } from "../../user/hooks/useUser"
import type { Role } from "../models/role"
import { RoleSchema } from "../models/role"
import type { DrawerMode } from "./RoleDrawer"
import RoleDrawer from "./RoleDrawer"

export default function RoleList() {
  const { t } = useTranslation()
  const { data: userData } = useUser()
  const canManage = userData?.can?.manageRoles ?? false

  const [search, setSearch] = useState("")
  const [debouncedSearch] = useDebouncedValue(search, 400)

  const { data, isLoading, page, pageSize, sort, sortDir, handleSortChange, handlePageChange, updateFilters } =
    useDataTable<Role>({
      queryKey: "admin-roles",
      url: "admin-roles",
      schema: RoleSchema,
      defaultSort: { field: "name", dir: "asc" },
      defaultPageSize: 50,
    })

  useEffect(() => {
    if (debouncedSearch) {
      updateFilters({ search: debouncedSearch })
    } else {
      updateFilters({})
    }
  }, [debouncedSearch, updateFilters])

  const [drawerOpened, setDrawerOpened] = useState(false)
  const [drawerMode, setDrawerMode] = useState<DrawerMode>("view")
  const [selectedRoleName, setSelectedRoleName] = useState<string | null>(null)

  const openDrawer = useCallback((name: string | null, mode: DrawerMode) => {
    setSelectedRoleName(name)
    setDrawerMode(mode)
    setDrawerOpened(true)
  }, [])

  const closeDrawer = useCallback(() => {
    setDrawerOpened(false)
    setSelectedRoleName(null)
  }, [])

  const handleDeleted = useCallback(() => { closeDrawer() }, [closeDrawer])
  const handleSwitchToEdit = useCallback(() => { setDrawerMode("edit") }, [])

  const columns: Column<Role>[] = [
    {
      key: "name",
      label: t("adminRoles.role"),
      sortable: true,
      render: (v) => (
        <Text fw={500} size="sm">
          {v.name}
        </Text>
      ),
    },
    {
      key: "description",
      label: t("adminRoles.description"),
      sortable: false,
      render: (v) => (
        <Text size="sm" c="dimmed" lineClamp={1}>
          {v.description ?? "—"}
        </Text>
      ),
    },
    {
      key: "user_count",
      label: t("adminRoles.users"),
      sortable: false,
      width: 80,
      render: (v) => <Badge variant="light" size="sm">{v.user_count}</Badge>,
    },
    {
      key: "permission_count",
      label: t("adminRoles.permissions"),
      sortable: false,
      width: 100,
      render: (v) => <Badge variant="light" color="blue" size="sm">{v.permission_count}</Badge>,
    },
  ]

  return (
    <PageLayout
      header={
        <PageHeader
          title={t("adminRoles.title")}
          actions={
            <Group>
              {canManage && (
                <Button onClick={() => openDrawer(null, "create")} leftSection={<i className="fa fa-plus" />}>
                  <Trans i18nKey="adminRoles.create" />
                </Button>
              )}
            </Group>
          }
        />
      }
    >
      <Group mb="md">
        <TextInput
          placeholder={t("adminRoles.searchPlaceholder")}
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          leftSection={<i className="fa fa-search" style={{ fontSize: 14 }} />}
          style={{ flex: 1, maxWidth: 400 }}
        />
      </Group>

      <DataTable<Role>
        columns={columns}
        data={data?.items ?? []}
        total={data?.total ?? 0}
        page={page}
        pageSize={pageSize}
        sort={sort}
        sortDir={sortDir}
        loading={isLoading}
        emptyMessage={t("adminRoles.empty")}
        keyExtractor={(v) => v.name}
        onSortChange={handleSortChange}
        onPageChange={handlePageChange}
        onRowClick={(v) => openDrawer(v.name, "view")}
        actions={(v) =>
          canManage ? (
            <Group gap={4} justify="flex-end" wrap="nowrap">
              <Tooltip label={t("common.edit")}>
                <ActionIcon variant="subtle" size="md" onClick={() => openDrawer(v.name, "edit")}>
                  <i className="fa fa-pencil" />
                </ActionIcon>
              </Tooltip>
            </Group>
          ) : undefined
        }
      />

      <RoleDrawer
        opened={drawerOpened}
        onClose={closeDrawer}
        mode={drawerMode}
        selectedRoleName={selectedRoleName}
        onDeleted={handleDeleted}
        onSwitchToEdit={handleSwitchToEdit}
      />
    </PageLayout>
  )
}
